import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { NOTIFICATIONS_TYPES } from 'infrastructure/notification/constants';
import { setNotificationResponse } from 'infrastructure/notification/redux/actions';
import { toInterruptionListCardView } from '../shared/helpers/interruptionMappers';
import useInterruptionService from '../useInterruptionService';
import { InterruptionEmployeeResponseStatus, InterruptionViewStatus } from '../types';
import { useInterruptionsContext } from '../InterruptionsContext';
import { useToast } from 'framework/ui/Toast';

const useEmployeePortalInterruptionsController = (isOpen: boolean) => {
  const { error: showErrorToast } = useToast();
  const dispatch = useDispatch();
  const { updateInterruptionStatus, updateInterruptionsViewStatus } = useInterruptionService();
  const sentSeenIdsRef = useRef<Set<string>>(new Set());
  const {
    error,
    listInterruptions,
    loading,
    removeInterruption,
    setInterruptionsViewStatus,
    sendInterruptionTeamsResponseMessage,
  } = useInterruptionsContext();

  const interruptionsCount = listInterruptions.length;
  const items = useMemo(() => listInterruptions.map(toInterruptionListCardView), [listInterruptions]);

  const handleUpdateStatus = useCallback(
    async (id: string, status: InterruptionEmployeeResponseStatus): Promise<boolean> => {
      try {
        await updateInterruptionStatus(id, status);
        await sendInterruptionTeamsResponseMessage(id, status);

        const nextCount = Math.max(interruptionsCount - 1, 0);

        removeInterruption(id);
        dispatch(
          setNotificationResponse({
            response: { count: nextCount },
            type: NOTIFICATIONS_TYPES.INTERRUPTION,
          }),
        );

        return true;
      } catch (updateError) {
        showErrorToast('Failed to update interruption status');
        return false;
      }
    },
    [
      dispatch,
      interruptionsCount,
      removeInterruption,
      showErrorToast,
      updateInterruptionStatus,
      sendInterruptionTeamsResponseMessage,
    ],
  );

  useEffect(() => {
    if (!isOpen || listInterruptions.length === 0) {
      return;
    }

    const unreadIds = listInterruptions
      .filter(item => item.viewStatus === InterruptionViewStatus.Unread && !sentSeenIdsRef.current.has(item.id))
      .map(item => item.id);

    if (unreadIds.length === 0) {
      return;
    }

    unreadIds.forEach(id => sentSeenIdsRef.current.add(id));

    updateInterruptionsViewStatus(unreadIds, InterruptionViewStatus.Seen)
      .then(() => {
        setInterruptionsViewStatus({ ids: unreadIds, viewStatus: InterruptionViewStatus.Seen });
      })
      .catch(() => {
        unreadIds.forEach(id => sentSeenIdsRef.current.delete(id));
      });
  }, [isOpen, listInterruptions, setInterruptionsViewStatus, updateInterruptionsViewStatus]);

  return {
    error,
    interruptionsCount,
    items,
    loading,
    onUpdateStatus: handleUpdateStatus,
  };
};

export default useEmployeePortalInterruptionsController;
